import i18n from "../i18n";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { createOperation, enqueueEvidence, enqueueOperation, getDeviceId, isNetworkAvailable, isOfflineAllowedEndpoint, listOperations, secureCachedFetch, syncPendingOperations, syncSummary } from "@/lib/offlineSync";
import { compressImage, validateUpload } from "@/lib/uploads";
import { farmDateIso } from "@/lib/farmTime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VoiceTextarea from "@/components/VoiceTextarea";
import { Utensils, Syringe, Baby, Scale, QrCode, CheckCircle2, Clock, ListTodo, RefreshCw, CloudOff, Camera, Droplets, HeartPulse, Pill, Skull, Stethoscope, MapPin, Sprout, Wheat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { permittedFieldAction } from "@/lib/offlineQuickActions";

const PRIORITY_COLOR = {
    urgent: "bg-destructive/10 text-destructive border-destructive/40",
    high: "bg-amber-500/10 text-amber-700 border-amber-500/40",
    medium: "bg-blue-500/10 text-blue-700 border-blue-500/40",
    low: "bg-secondary text-muted-foreground border-border",
};

const FIELD_OPS_ACTIONS = [
  // Note: the `perm` string maps to a backend field-mode-permission action.
  { key: "feeding", perm: "feed", icon: Utensils, label: "LOG FEEDING", title: "Feeding", color: "bg-primary text-white" },
  { key: "water", perm: "water", icon: Droplets, label: "WATER CHECK", title: "Water Check", color: "bg-cyan-600 text-white" },
  { key: "health_inspection", perm: "inspect", icon: Stethoscope, label: "HEALTH INSPECTION", title: "Health Inspection", color: "bg-emerald-600 text-white" },
  { key: "medication", perm: "treat", icon: Pill, label: "MEDICATION", title: "Medication", color: "bg-fuchsia-600 text-white" },
  { key: "vaccination", perm: "vaccinate", icon: Syringe, label: "LOG VACCINATION", title: "Vaccination", color: "bg-blue-600 text-white" },
  { key: "weigh", perm: "weigh", icon: Scale, label: "WEIGH-IN", title: "Weight Recording", color: "bg-amber-600 text-white" },
  { key: "mortality", perm: "mortality", icon: Skull, label: "MORTALITY", title: "Mortality", color: "bg-rose-600 text-white" },
  { key: "breeding", perm: "breeding", icon: HeartPulse, label: "BREEDING", title: "Breeding", color: "bg-indigo-600 text-white" },
  { key: "pregnancy_check", perm: "pregnancy", icon: HeartPulse, label: "PREGNANCY", title: "Pregnancy Check", color: "bg-violet-600 text-white" },
  { key: "farrowing", perm: "farrowing", icon: Baby, label: "FARROWING", title: "Farrowing", color: "bg-pink-600 text-white" },
  { key: "plant", perm: "plant", icon: Sprout, label: "PLANT CROP", title: "Plant Crop", color: "bg-green-700 text-white" },
  { key: "spray", perm: "spray", icon: Droplets, label: "LOG SPRAY", title: "Crop Spray", color: "bg-lime-700 text-white" },
  { key: "irrigate", perm: "irrigate", icon: Droplets, label: "LOG IRRIGATION", title: "Crop Irrigation", color: "bg-sky-700 text-white" },
  { key: "harvest", perm: "harvest", icon: Wheat, label: "LOG HARVEST", title: "Crop Harvest", color: "bg-orange-700 text-white" },
];

const QUICK_ACTIONS = FIELD_OPS_ACTIONS;
const ACTION_BY_KEY = Object.fromEntries(FIELD_OPS_ACTIONS.map((action) => [action.key, action]));
function defaultFormFor(actionKey) {
  const today = farmDateIso();
  switch (actionKey) {
    case "feeding":
      return { date: today, animal_id: "", animal_tag: "", feed_qty: "", unit: "kg", feed_type: "", batch_label: "", notes: "" };
    case "water":
      return { date: today, animal_id: "", animal_tag: "", location: "", water_quality: "good", water_level: "", notes: "" };
    case "health_inspection":
      return { date: today, animal_id: "", animal_tag: "", condition: "healthy", temperature_c: "", notes: "" };
    case "medication":
      return { date: today, animal_id: "", animal_tag: "", medication_name: "", dose: "", route: "", notes: "" };
    case "vaccination":
      return { date: today, animal_id: "", animal_tag: "", vaccine_name: "", dose: "", next_due_date: "", notes: "" };
    case "weigh":
      return { date: today, animal_id: "", animal_tag: "", weight_kg: "", feed_kg: "", notes: "" };
    case "mortality":
      return { date: today, animal_id: "", animal_tag: "", cause: "", disposal_method: "", notes: "" };
    case "breeding":
      return { date: today, dam_id: "", dam_tag: "", sire_id: "", sire_tag: "", method: "natural", service_date: today, heat_date: "", technician: "", notes: "" };
    case "pregnancy_check":
      return { date: today, animal_id: "", animal_tag: "", result: "pregnant", estimated_due_date: "", notes: "" };
    case "farrowing":
      return {
        date: today,
        dam_id: "",
        dam_tag: "",
        litter_size: "",
        live_born: "",
        stillborn: "",
        mummified: "",
        weak_piglets: "",
        birth_weight: "",
        complications: "",
        assistance_required: "no",
        notes: "",
      };
    case "plant":
      return { date: today, plot_name: "", crop: "", area_ha: "", expected_harvest_date: "", notes: "" };
    case "spray":
    case "irrigate":
      return { date: today, plot_id: "", input_name: "", quantity: "", unit: "", notes: "" };
    case "harvest":
      return { date: today, plot_id: "", crop: "", yield_kg: "", grade: "", notes: "" };
    default:
      return { date: today, notes: "" };
  }
}

export default function FieldModePage() {
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [species, setSpecies] = useState([]);
    const [filter, setFilter] = useState("today");
    const [action, setAction] = useState(null);
    const [form, setForm] = useState({});
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [busy, setBusy] = useState(false);
    const [cacheInfo, setCacheInfo] = useState({ fromCache: false, savedAt: null });
    const [allowedActions, setAllowedActions] = useState(null);
    const [plots, setPlots] = useState([]);
    const [syncState, setSyncState] = useState({ pending: 0, failed: 0, reviewRequired: 0, lastSuccessfulSync: null });

    // Load per-user Field Mode action permissions (Phase 3: role-based access).
    useEffect(() => {
        if (!user?.id) return;
        secureCachedFetch(user.id, "field-permissions", () => api.get("/field-mode-permissions/me").then((r) => r.data))
        .then((result) => setAllowedActions(new Set(result.data.actions || [])))
        .catch(() => setAllowedActions(new Set()));
    }, [user?.id]);
    const visibleActions = allowedActions
      ? QUICK_ACTIONS.filter(a => allowedActions.has(a.perm))
        : [];

    useEffect(() => {
      if (!action) return;
      setForm(defaultFormFor(action));
      setPhotoFile(null);
      setPhotoPreview("");
    }, [action]);

    const openAction = useCallback((key) => {
      setAction(key);
      setBusy(false);
      setForm(defaultFormFor(key));
      setPhotoFile(null);
      setPhotoPreview("");
    }, []);

    const clearActionState = (key) => {
      setAction(null);
      setForm({});
      setPhotoFile(null);
      setPhotoPreview("");
    };


const load = useCallback(async () => {
  const [t, a, s, p] = await Promise.all([
    secureCachedFetch(user.id, "field:tasks", () =>
      api.get("/tasks").then((r) => r.data)
    ).catch(() => ({
      data: [],
      fromCache: false,
      savedAt: 0,
    })),

    secureCachedFetch(user.id, "field:animals", () => api.get("/animals").then((r) => (
      Array.isArray(r.data) ? r.data : r.data?.animals || r.data?.items || []
    ))).catch(() => ({ data: [], fromCache: false, savedAt: 0 })),

    secureCachedFetch(user.id, "field:species", () =>
      api.get("/species").then((r) => r.data)
    ).catch(() => ({
      data: [],
      fromCache: false,
      savedAt: 0,
    })),
    secureCachedFetch(user.id, "field:crop-plots", () => api.get("/crop-plots").then((r) => r.data)).catch(() => ({
      data: [], fromCache: false, savedAt: 0,
    })),
  ]);

  setTasks(Array.isArray(t.data) ? t.data : []);
  setAnimals(Array.isArray(a.data) ? a.data : []);
  setSpecies(Array.isArray(s.data) ? s.data : []);
  setPlots(Array.isArray(p.data) ? p.data : []);

  const fromCache = Boolean(
    t.fromCache || a.fromCache || s.fromCache || p.fromCache
  );

  const savedAt = Math.max(
    Number(t.savedAt || 0),
    Number(a.savedAt || 0),
    Number(s.savedAt || 0),
    Number(p.savedAt || 0)
  );

  setCacheInfo({
    fromCache,
    savedAt,
  });
}, [user?.id]);

useEffect(() => {
  load();
}, [load]);
    // My tasks (assigned to me OR unassigned)
    const today = farmDateIso();
    const weekEnd = farmDateIso(new Date(Date.now() + 7 * 86400000));

    const refreshSyncState = useCallback(async () => {
      if (!user?.id) return;
      try { setSyncState(await syncSummary(user.id)); } catch { /* unsupported private mode stays online-only */ }
    }, [user?.id]);

    const runSync = useCallback(async () => {
      if (!user?.id || !isNetworkAvailable()) return { attempted: 0, synced: 0, remaining: syncState.pending };
      const result = await syncPendingOperations({
          userId: user.id,
          postOperation: (operation) => api.post("/offline-sync/operations", operation, { timeout: 10000 }),
          uploadEvidence: async (file, metadata) => {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("source_operation_id", metadata.operationId);
            fd.append("evidence_id", metadata.evidenceId);
            fd.append("captured_at", metadata.capturedAt);
            const response = await api.post("/upload", fd, { timeout: 15000 });
            return response.data || {};
          },
      });
      await refreshSyncState();
      if (result.synced) await load();
      return result;
    }, [load, refreshSyncState, syncState.pending, user?.id]);

    useEffect(() => {
      refreshSyncState();
      const changed = () => refreshSyncState();
      window.addEventListener("ajfarm:sync-updated", changed);
      return () => {
        window.removeEventListener("ajfarm:sync-updated", changed);
      };
    }, [refreshSyncState]);

    const saveFieldOperation = async ({ operationId, module, action: operationAction, endpoint, payload, evidenceIds = [], serverKnownRevision = null }) => {
      const operation = createOperation({
        userId: user?.id,
        deviceId: getDeviceId(),
        module,
        action: operationAction,
        endpoint,
        payload,
        evidenceIds,
        serverKnownRevision,
        operationId,
      });
      await enqueueOperation(operation);
      await refreshSyncState();
      if (!isNetworkAvailable()) return { status: "pending" };
      await runSync();
      const saved = (await listOperations(user.id)).find((row) => row.operationId === operationId);
      return { status: saved?.status || "pending" };
    };

    useEffect(() => {
      if (allowedActions === null) return;
      const requested = searchParams.get("action");
      if (!requested) return;
      const permitted = permittedFieldAction(requested, FIELD_OPS_ACTIONS, allowedActions);
      if (permitted) openAction(permitted);
      else toast.error(i18n.t("common.permission_denied", { defaultValue: "This action is not available for your account." }));
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }, [allowedActions, openAction, searchParams, setSearchParams]);

    const myTasks = tasks.filter(t => {
        if (t.status === "done") return false;
        const assignees = t.assignee_ids || [];
        // Show if assigned to me, unassigned, or I'm owner/admin
        const mine = assignees.length === 0 || assignees.includes(user?.id) || assignees.includes(user?.employee_id) || user?.role === "owner";
        if (!mine) return false;
        const due = t.due_date || "";
        if (filter === "today") return due <= today;
        if (filter === "week") return due <= weekEnd;
        return true;  // 'all'
    }).sort((a, b) => (a.due_date || "9999") .localeCompare(b.due_date || "9999"));

    const markDone = async (task) => {
        try {
            const result = await saveFieldOperation({
              operationId: `field-task-${task.id}`,
              module: "tasks", action: "complete", endpoint: `/tasks/${task.id}/complete`,
              payload: {}, serverKnownRevision: task.updated_at || task.revision || null,
            });
            toast.success(result.status === "synced" ? `✓ ${task.title}` : "Task completion saved on this device");
            load();
        } catch (e) {
            toast.error(e.message || "Task completion could not be stored safely");
        }
    };

    const submitAction = async () => {
      if (!action) return;
      setBusy(true);
      try {
        const submittedAt = new Date().toISOString();
        const submissionId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const basePayload = {
          date: form.date || today,
          timestamp: submittedAt,
          submitted_at: submittedAt,
          employee_id: user?.id || "",
          employee_name: user?.name || "",
          source_module: "field_mode",
          source_action: action,
          source_submission_id: submissionId,
          gps_lat: form.gps_lat ?? null,
          gps_lng: form.gps_lng ?? null,
        };
        const postOperational = async (endpoint, payload) => {
          if (!isOfflineAllowedEndpoint(endpoint)) {
            if (!isNetworkAvailable()) throw new Error(i18n.t("offline.internet_required"));
            let onlinePayload = payload;
            if (photoFile) {
              const [valid, message] = validateUpload(photoFile);
              if (!valid) throw new Error(message);
              const compressed = await compressImage(photoFile);
              const body = new FormData();
              body.append("file", compressed);
              const response = await api.post("/upload", body);
              onlinePayload = { ...payload, photo_file_id: response.data?.id, photo_filename: response.data?.original_filename, photo_pending: false };
            }
            return api.post(endpoint, onlinePayload);
          }
          const evidenceIds = [];
          if (photoFile) {
            const [valid, message] = validateUpload(photoFile);
            if (!valid) throw new Error(message);
            const compressed = await compressImage(photoFile);
            const evidenceId = `${submissionId}:photo:1`;
            await enqueueEvidence({ evidenceId, operationId: submissionId, userId: user?.id, file: compressed, capturedAt: submittedAt });
            evidenceIds.push(evidenceId);
          }
          const result = await saveFieldOperation({
            operationId: submissionId,
            module: action,
            action: "create",
            endpoint,
            payload,
            evidenceIds,
          });
          if (result.status !== "synced") {
            const pendingError = new Error(result.status === "review_required" ? "Saved — Owner review required" : "Saved safely on this device — sync pending");
            pendingError.offlineSaved = true;
            throw pendingError;
          }
          return result;
        };

        const animalById = new Map((animals || []).map((item) => [item.id, item]));
        const resolveTag = (id) => {
          const row = animalById.get(id || "");
          return row ? (row.tag_id || row.name || row.id) : "";
        };
        const animalMeta = {
          animal_tag: form.animal_tag || resolveTag(form.animal_id),
          dam_tag: form.dam_tag || resolveTag(form.dam_id),
          sire_tag: form.sire_tag || resolveTag(form.sire_id),
        };

        const photoMeta = photoFile ? { photo_pending: true, photo_capture_timestamp: submittedAt } : {};

        if (action === "feeding") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          const feedKg = Number(String(form.feed_qty ?? "").trim());
          if (!Number.isFinite(feedKg) || feedKg <= 0) {
            toast.error("Enter a valid feed quantity.");
            return;
          }
          const payload = {
            ...basePayload,
            source_submission_id: submissionId,
            animal_id: form.animal_id || "",
            animal_tag: form.animal_tag || resolveTag(form.animal_id),
            batch_label: form.batch_label || "",
            feed_type: form.feed_type || "",
            feed_kg: feedKg,
            unit: form.unit || "kg",
            notes: form.notes || "",
            ...photoMeta,
          };
          await postOperational("/feeding-records", payload);
          toast.success("Feeding logged ✓");
        } else if (action === "water") {
          await postOperational("/water-checks", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: form.animal_tag || resolveTag(form.animal_id),
            location: form.location || "",
            water_quality: form.water_quality || "good",
            water_level: form.water_level || "",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Water check recorded ✓");
        } else if (action === "health_inspection") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          await postOperational("/health-inspections", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            condition: form.condition || "healthy",
            temperature_c: form.temperature_c || "",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Health inspection recorded ✓");
        } else if (action === "medication") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          await postOperational("/treatments", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            treatment_type: "medication",
            medicine_name: form.medication_name || "",
            dose: form.dose || "",
            route: form.route || "",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Medication logged ✓");
        } else if (action === "vaccination") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          await postOperational("/vaccination-records", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            vaccine_name: form.vaccine_name || "",
            dose: form.dose || "",
            scheduled_date: form.next_due_date || form.date || today,
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Vaccination recorded ✓");
        } else if (action === "weigh") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          const rawWeightValue = String(form.weight_kg ?? "").trim();
          const newWeight = Number(rawWeightValue);
          const rawFeedValue = String(form.feed_kg ?? "").trim();
          const feedAmount = rawFeedValue === "" ? 0 : Number(rawFeedValue);

          if (!rawWeightValue || Number.isNaN(newWeight) || newWeight <= 0) {
            toast.error("Please enter a valid weight greater than 0.");
            return;
          }

          if (newWeight > 5000) {
            toast.error("Weight seems unusually high. Please check the entry.");
            return;
          }

          if (rawFeedValue !== "" && (Number.isNaN(feedAmount) || feedAmount < 0)) {
            toast.error("Please enter a valid feed value greater than or equal to 0.");
            return;
          }

          await postOperational("/weight-records", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            weight_kg: newWeight,
            feed_kg: feedAmount,
            notes: form.notes || "",
            ...photoMeta,
          });

          setAnimals((previousAnimals) =>
            previousAnimals.map((animal) =>
              animal.id === form.animal_id
                ? {
                  ...animal,
                  weight_kg: newWeight,
                  current_weight: newWeight,
                  weight: newWeight,
                  custom_fields: {
                    ...(animal.custom_fields || {}),
                    weight_kg: String(newWeight),
                  },
                }
                : animal
            )
          );

          toast.success("Weigh-in recorded ✓");
        } else if (action === "mortality") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          await postOperational("/mortality-records", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            cause: form.cause || "",
            disposal_method: form.disposal_method || "",
            notes: form.notes || "",
            status: "deceased",
            ...photoMeta,
          });
          toast.success("Mortality recorded ✓");
        } else if (action === "breeding") {
          if (!form.dam_id) {
            toast.error("Please select a dam / mother animal.");
            return;
          }
          if (!form.sire_id) {
            toast.error("Please select a sire / male animal.");
            return;
          }
          await postOperational("/breeding-records", {
            ...basePayload,
            source_submission_id: submissionId,
            animal_id: form.dam_id || "",
            animal_tag: animalMeta.dam_tag,
            dam_id: form.dam_id || "",
            dam_tag: animalMeta.dam_tag,
            female_id: form.dam_id || "",
            female_tag: animalMeta.dam_tag,
            sire_id: form.sire_id || "",
            sire_tag: animalMeta.sire_tag,
            male_id: form.sire_id || "",
            male_tag: animalMeta.sire_tag,
            method: (form.method || "natural").toLowerCase(),
            service_date: form.service_date || form.date || today,
            heat_date: form.heat_date || "",
            technician: form.technician || "",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Breeding recorded ✓");
        } else if (action === "pregnancy_check") {
          if (!form.animal_id) {
            toast.error("Please select an animal.");
            return;
          }
          await postOperational("/pregnancy-checks", {
            ...basePayload,
            animal_id: form.animal_id || "",
            animal_tag: animalMeta.animal_tag,
            result: form.result || "pregnant",
            estimated_due_date: form.estimated_due_date || "",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Pregnancy check recorded ✓");
        } else if (action === "farrowing") {
          if (!form.dam_id) {
            toast.error("Please select a dam / mother animal.");
            return;
          }
          await postOperational("/farrowing-records", {
            ...basePayload,
            source_submission_id: submissionId,
            dam_id: form.dam_id || "",
            dam_tag: animalMeta.dam_tag,
            farrowing_date: form.date || today,
            litter_size: Number(form.litter_size) || 0,
            live_born: Number(form.live_born) || 0,
            stillborn: Number(form.stillborn) || 0,
            mummified: Number(form.mummified) || 0,
            weak_piglets: Number(form.weak_piglets) || 0,
            birth_weight: form.birth_weight === "" ? null : Number(form.birth_weight),
            complications: form.complications || "",
            assistance_required: form.assistance_required || "no",
            notes: form.notes || "",
            ...photoMeta,
          });
          toast.success("Farrowing recorded ✓");
        } else if (action === "plant") {
          if (!form.plot_name || !form.crop) { toast.error("Plot name and crop are required."); return; }
          await postOperational("/crop-plots", {
            ...basePayload, name: form.plot_name, crop: form.crop,
            area_ha: Number(form.area_ha) || 0, planted_date: form.date || today,
            expected_harvest_date: form.expected_harvest_date || "", stage: "planted", status: "active",
            notes: form.notes || "",
          });
          toast.success("Crop planting recorded ✓");
        } else if (action === "spray" || action === "irrigate") {
          if (!form.plot_id) { toast.error("Please select a crop plot."); return; }
          await postOperational("/crop-activities", {
            ...basePayload, plot_id: form.plot_id, field_action: action,
            activity_type: action === "spray" ? "pesticide" : "irrigation",
            input_name: form.input_name || "", quantity: Number(form.quantity) || 0,
            unit: form.unit || "", notes: form.notes || "",
          });
          toast.success(`${action === "spray" ? "Crop spray" : "Irrigation"} recorded ✓`);
        } else if (action === "harvest") {
          if (!form.plot_id) { toast.error("Please select a crop plot."); return; }
          await postOperational("/crop-harvests", {
            ...basePayload, plot_id: form.plot_id, crop: form.crop || "",
            yield_kg: Number(form.yield_kg) || 0, grade: form.grade || "", notes: form.notes || "",
          });
          toast.success("Crop harvest recorded ✓");
        }

        clearActionState(action);
        await load();
      } catch (e) {
        if (e?.offlineSaved) {
          clearActionState(action);
          toast.info(e.message);
        } else {
          toast.error(e.response?.data?.detail || e.message || "Save failed");
        }
      } finally {
        setBusy(false);
      }
    };

const activeAnimals = Array.isArray(animals)
  ? animals.filter((a) => {
      const status = String(a.status || "active").toLowerCase();
      return status === "active" || status === "alive" || status === "available";
    })
  : [];
    const speciesMap = Object.fromEntries(species.map(s => [s.id, s]));
    const actionConfig = ACTION_BY_KEY[action] || null;
    const selectedAnimalId = form.animal_id || form.dam_id || null;
    const selectedAnimal = selectedAnimalId
      ? activeAnimals.find((animal) => animal.id === selectedAnimalId) || null
      : null;

    const resolveAnimalWeight = (animal) => {
        const candidates = [
            animal?.custom_fields?.weight_kg,
            animal?.weight_kg,
            animal?.current_weight,
            animal?.weight,
        ];

        for (const value of candidates) {
            if (value === null || value === undefined || value === "") continue;
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }

        return null;
    };

    const currentWeight = selectedAnimal ? resolveAnimalWeight(selectedAnimal) : null;
    const proposedWeight = (() => {
        if (form.weight_kg === "" || form.weight_kg === null || form.weight_kg === undefined) return null;
        const parsed = Number(form.weight_kg);
        return Number.isNaN(parsed) ? null : parsed;
    })();
    const feedSinceLast = (() => {
        if (form.feed_kg === "" || form.feed_kg === null || form.feed_kg === undefined) return null;
        const parsed = Number(form.feed_kg);
        return Number.isNaN(parsed) ? null : parsed;
    })();
    const weightDifference = currentWeight != null && proposedWeight != null ? proposedWeight - currentWeight : null;
    const changePercentage = currentWeight != null && currentWeight !== 0 && proposedWeight != null
        ? ((proposedWeight - currentWeight) / currentWeight) * 100
        : null;
    const estimatedFcr = weightDifference != null && weightDifference > 0 && feedSinceLast != null && feedSinceLast > 0
        ? feedSinceLast / weightDifference
        : null;
    const targetWeight = (() => {
        const candidateValues = [
            selectedAnimal?.target_weight,
            speciesMap[selectedAnimal?.species_id]?.target_weight,
        ];
        for (const value of candidateValues) {
            if (value === null || value === undefined || value === "") continue;
            const parsed = Number(value);
            if (!Number.isNaN(parsed) && parsed > 0) return parsed;
        }
        return null;
    })();
    const remainingWeight = currentWeight != null && targetWeight != null ? targetWeight - currentWeight : null;
    const progressPercentage = currentWeight != null && targetWeight != null && targetWeight > 0
        ? Math.min(100, Math.max(0, (currentWeight / targetWeight) * 100))
        : null;
    const growthStatus = (() => {
        if (weightDifference != null && weightDifference < 0) return "Weight decreased — inspect animal";
        if (weightDifference === 0) return "No weight change";
        if (changePercentage != null && changePercentage > 15) return "Excellent growth";
        if (changePercentage != null && changePercentage > 5) return "Good growth";
        if (changePercentage != null && changePercentage > 0) return "Slow growth";
        return null;
    })();
    const growthStatusClass = (() => {
        if (!growthStatus) return "";
        if (growthStatus === "Weight decreased — inspect animal") return "border-rose-500/40 bg-rose-500/10 text-rose-700";
        if (growthStatus === "No weight change" || growthStatus === "Slow growth") return "border-amber-500/40 bg-amber-500/10 text-amber-700";
        return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
    })();

    const captureGps = () => {
      if (!navigator.geolocation) {
        toast.error("GPS is not available on this device.");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm((current) => ({
            ...current,
            gps_lat: Number(position.coords.latitude.toFixed(6)),
            gps_lng: Number(position.coords.longitude.toFixed(6)),
          }));
          toast.success("Location captured");
        },
        () => toast.error("Could not read GPS location")
      );
    };

    const handlePhotoChange = (event) => {
      const file = event.target.files?.[0] || null;
      setPhotoFile(file);
      if (!file) {
        setPhotoPreview("");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotoPreview(String(reader.result || ""));
      reader.readAsDataURL(file);
    };

    return (
        <div className="max-w-2xl mx-auto space-y-4 pb-8" data-testid="field-mode-page">
            {/* Header */}
            <div className="flex items-center justify-between px-1">
                <div>
                    <div className="tiny-label">{i18n.t("batch4.85e57a7cc344")}</div>
                    <h1 className="text-2xl font-bold">{i18n.t("batch4.94dd9e08c129")} {user?.name || "Team"}</h1>
                </div>
                <Button variant="outline" size="sm" onClick={load} className="h-10" data-testid="field-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            </div>

            {cacheInfo.fromCache && (
                <div
                    className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-800 px-3 py-2 text-xs font-medium"
                    data-testid="field-offline-notice"
                >
                    <CloudOff className="w-4 h-4" />
                    {i18n.t("batch4.28445477d039")}{cacheInfo.savedAt ? ` from ${new Date(cacheInfo.savedAt).toLocaleString()}` : ""}{i18n.t("batch4.d45543933c89")}
                </div>
            )}

            {/* Quick action buttons — huge, thumb-reachable. Filtered by role permissions. */}
            <div className="grid grid-cols-2 gap-3">
                {visibleActions.length === 0 && (
                    <div className="col-span-2 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 text-sm" data-testid="field-no-actions-msg">
                        {i18n.t("batch4.c57627ab8ffd")}
                    </div>
                )}
                {visibleActions.map(a => {
                    const Icon = a.icon;
                    return (
                    <button key={a.key} onClick={() => openAction(a.key)}
                            className={`${a.color} rounded-2xl p-6 flex flex-col items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform min-h-[110px]`}
                            data-testid={`field-action-${a.key}`}>
                            <Icon className="w-10 h-10" />
                            <span className="text-sm font-bold tracking-wide">{a.label}</span>
                        </button>
                    );
                })}
                <button onClick={() => nav("/scan")}
                    className="col-span-2 bg-secondary text-foreground rounded-2xl p-4 flex items-center justify-center gap-3 shadow active:scale-95 transition-transform min-h-[72px]"
                    data-testid="field-action-scan">
                    <QrCode className="w-7 h-7" />
                    <span className="text-lg font-bold">{i18n.t("batch4.158eadb6b85b")}</span>
                </button>
            </div>

            {/* My Tasks */}
            <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <ListTodo className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold">{i18n.t("batch4.54bd9e0bc82a")}</h3>
                    <Badge variant="secondary" className="ml-1">{myTasks.length}</Badge>
                    <div className="ml-auto flex gap-1">
                        {["today", "week", "all"].map(f => (
                            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className="h-8 text-xs capitalize" onClick={() => setFilter(f)} data-testid={`field-filter-${f}`}>{f}</Button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    {myTasks.slice(0, 20).map(t => (
                        <div key={t.id} className="border border-border rounded-xl p-3 flex items-start gap-3" data-testid={`field-task-${t.id}`}>
                            <Button size="icon" variant="outline" onClick={() => markDone(t)} className="h-11 w-11 flex-shrink-0 rounded-full" data-testid={`field-task-done-${t.id}`}>
                                <CheckCircle2 className="w-5 h-5" />
                            </Button>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm">{t.title}</div>
                                {t.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.description}</div>}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {t.due_date && <span className="text-xs font-mono text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.due_date}</span>}
                                    {t.priority && <Badge variant="outline" className={`text-xs ${PRIORITY_COLOR[t.priority] || ""}`}>{t.priority}</Badge>}
                                    {t.category && <Badge variant="secondary" className="text-xs">{t.category}</Badge>}
                                </div>
                            </div>
                        </div>
                    ))}
                    {myTasks.length === 0 && (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                            <CheckCircle2 className="w-10 h-10 mx-auto text-primary opacity-40 mb-2" />
                            {filter === "today" ? "No tasks due today. Great job!" : "No open tasks."}
                        </div>
                    )}
                </div>
            </Card>

            {/* Action dialog — simple 2-field forms */}
            <Dialog open={!!action} onOpenChange={o => !o && setAction(null)}>
                <DialogContent data-testid="field-action-dialog">
                    <DialogHeader>
                    <DialogTitle className="capitalize">{actionConfig?.title || action?.replace("_", " ")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch4.707d5f9c8662")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 rounded-xl border bg-muted/30 p-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Button type="button" variant="outline" className="h-11" onClick={captureGps}>
                          <MapPin className="mr-2 h-4 w-4" /> {i18n.t("batch4.bdd143cbfa6a")}
                        </Button>
                        <label className="flex h-11 cursor-pointer items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
                          <Camera className="mr-2 h-4 w-4" /> {i18n.t("batch4.d14349309573")}
                          <Input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoChange} />
                        </label>
                      </div>
                      {photoPreview && (
                        <img src={photoPreview} alt={i18n.t("batch4.cc8da61915b7")} className="max-h-48 w-full rounded-lg object-cover border" />
                      )}
                      {(form.gps_lat != null || form.gps_lng != null) && (
                        <div className="text-xs text-muted-foreground">
                          {i18n.t("batch4.8dd644347f3f")} {form.gps_lat ?? "—"}, {form.gps_lng ?? "—"}
                        </div>
                      )}
                    </div>
                        {action === "feeding" && <>
                      <Select value={form.animal_id || ""} onValueChange={(value) => {
                        const selected = activeAnimals.find((animal) => animal.id === value);
                        setForm({ ...form, animal_id: value, animal_tag: selected?.tag_id || selected?.name || value });
                      }}>
                        <SelectTrigger className="h-12 text-base" data-testid="field-feed-animal-select"><SelectValue placeholder={i18n.t("batch4.9815c7072808")} /></SelectTrigger>
                        <SelectContent>
                          {activeAnimals.map((animal) => (
                            <SelectItem key={animal.id} value={animal.id}>
                              {animal.tag_id || animal.name || animal.id} · {speciesMap[animal.species_id]?.name || "Unknown species"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input placeholder={i18n.t("batch4.5fc3bb63e1b3")} value={form.batch_label || ""} onChange={e => setForm({ ...form, batch_label: e.target.value })} className="h-12 text-base" autoFocus data-testid="field-feed-batch-input" />
                      <Input placeholder={i18n.t("batch4.9c6854a11b77")} value={form.feed_type || ""} onChange={e => setForm({ ...form, feed_type: e.target.value })} className="h-12 text-base" data-testid="field-feed-type-input" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" placeholder={i18n.t("batch4.84ee78e37e6a")} value={form.feed_qty || ""} onChange={e => setForm({ ...form, feed_qty: e.target.value })} className="h-12 text-base font-mono" data-testid="field-feed-qty-input" />
                        <Select value={form.unit || "kg"} onValueChange={(value) => setForm({ ...form, unit: value })}>
                          <SelectTrigger className="h-12 text-base" data-testid="field-feed-unit-select"><SelectValue placeholder={i18n.t("batch4.f6b935ab3371")} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">{i18n.t("batch4.1389845b02c0")}</SelectItem>
                            <SelectItem value="g">{i18n.t("batch4.54fd1711209f")}</SelectItem>
                            <SelectItem value="lb">{i18n.t("batch4.cba41814177b")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                            <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" data-testid="field-feed-notes-input" />
                        </>}
                        {action === "water" && <>
                      <Select value={form.animal_id || ""} onValueChange={(value) => {
                        const selected = activeAnimals.find((animal) => animal.id === value);
                        setForm({ ...form, animal_id: value, animal_tag: selected?.tag_id || selected?.name || value });
                      }}>
                        <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.64ed4adcf771")} /></SelectTrigger>
                        <SelectContent>
                          {activeAnimals.map((animal) => (
                            <SelectItem key={animal.id} value={animal.id}>
                              {animal.tag_id || animal.name || animal.id} · {speciesMap[animal.species_id]?.name || "Unknown species"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input placeholder={i18n.t("batch4.8d1999570cd4")} value={form.location || ""} onChange={e => setForm({ ...form, location: e.target.value })} className="h-12 text-base" autoFocus />
                      <Select value={form.water_quality || "good"} onValueChange={(value) => setForm({ ...form, water_quality: value })}>
                        <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.0a474887b300")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="good">{i18n.t("batch4.61dedcf053ff")}</SelectItem>
                          <SelectItem value="fair">{i18n.t("batch4.64b985898db9")}</SelectItem>
                          <SelectItem value="poor">{i18n.t("batch4.4e0fa4378f15")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder={i18n.t("batch4.8c833df0ba02")} value={form.water_level || ""} onChange={e => setForm({ ...form, water_level: e.target.value })} className="h-12 text-base" />
                      <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
                        </>}
{(action === "vaccination" || action === "weigh" || action === "health_inspection" || action === "medication" || action === "mortality" || action === "pregnancy_check") && (
  <>
    <Select
      value={form.animal_id || ""}
      onValueChange={(value) => {
        const selected = activeAnimals.find((animal) => animal.id === value);
        setForm({ ...form, animal_id: value, animal_tag: selected?.tag_id || selected?.name || value });
      }}
    >
      <SelectTrigger
        className="h-12 text-base"
        data-testid="field-animal-select"
      >
        <SelectValue placeholder={i18n.t("batch4.9815c7072808")} />
      </SelectTrigger>

      <SelectContent>
        {activeAnimals.map((animal) => (
          <SelectItem key={animal.id} value={animal.id}>
            {animal.tag_id || animal.name || animal.id} ·{" "}
            {speciesMap[animal.species_id]?.name || "Unknown species"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    {form.animal_id &&
      activeAnimals
        .filter((animal) => animal.id === form.animal_id)
        .map((animal) => (
          <div
            key={animal.id}
            className="rounded-xl border bg-muted/40 p-3 space-y-2"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">
                  {i18n.t("batch4.1ea27782a820")}
                </p>

                <p className="font-semibold">
                  {animal.tag_id || animal.name || animal.id}
                </p>
              </div>

              <span className="rounded-full border bg-background px-3 py-1 text-xs font-medium capitalize">
                {animal.status || "Active"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-background p-2">
                <p className="text-xs text-muted-foreground">
                  {i18n.t("batch4.83a7238826ab")}
                </p>
                <p className="font-medium">
                  {speciesMap[animal.species_id]?.name ||
                    "Not recorded"}
                </p>
              </div>

              <div className="rounded-lg bg-background p-2">
                <p className="text-xs text-muted-foreground">
                  {i18n.t("batch4.e301dd6062f7")}
                </p>
<p className="font-medium capitalize">
  {animal.sex || animal.gender || "Not recorded"}
</p>

<p className="font-medium">
                  {animal.pen_name ||
                    animal.pen ||
                    animal.location ||
                    "Not recorded"}
                </p>
              </div>

              <div className="rounded-lg bg-background p-2">
                <p className="text-xs text-muted-foreground">
                  {i18n.t("batch4.ac8f31224ab0")}
                </p>
                <p className="font-medium">
                  {action === "weigh" && currentWeight != null
                    ? `${currentWeight} kg`
                    : action === "weigh"
                      ? "Not recorded"
                      : (
                          animal.custom_fields?.weight_kg != null
                            ? `${animal.custom_fields.weight_kg} kg`
                            : animal.weight_kg != null
                            ? `${animal.weight_kg} kg`
                            : animal.current_weight != null
                            ? `${animal.current_weight} kg`
                            : animal.weight != null
                            ? `${animal.weight} kg`
                            : "Not recorded"
                        )}
                </p>
            </div>
          </div>
          </div>
))}
{action === "weigh" && selectedAnimal && (
  <div className="rounded-xl border bg-muted/30 p-3 space-y-2">
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.931cd0b708ee")}</p>
        <p className="mt-1 font-semibold">
          {currentWeight != null ? `${currentWeight} kg` : "Not recorded"}
        </p>
      </div>

      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.ef2c495072dd")}</p>
        <p className="mt-1 font-semibold">
          {proposedWeight != null ? `${proposedWeight} kg` : "Enter weight"}
        </p>
      </div>

      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.3aac98402a70")}</p>
        <p className="mt-1 font-semibold">
          {proposedWeight == null || weightDifference == null
            ? "—"
            : `${weightDifference > 0 ? "+" : ""}${weightDifference} kg`}
        </p>
      </div>

      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.7eb1b417a689")}</p>
        <p className="mt-1 font-semibold">
          {proposedWeight == null || changePercentage == null
            ? "—"
            : `${changePercentage > 0 ? "+" : ""}${changePercentage.toFixed(1)}%`}
        </p>
      </div>

      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.0bfac0bb1640")}</p>
        <p className="mt-1 font-semibold">
          {feedSinceLast != null ? `${feedSinceLast} kg` : "Enter feed"}
        </p>
      </div>

      <div className="rounded-xl border bg-background/70 p-3">
        <p className="text-xs text-muted-foreground">{i18n.t("batch4.96169cd67a1e")}</p>
        <p className="mt-1 font-semibold">
          {estimatedFcr != null ? estimatedFcr.toFixed(2) : "—"}
        </p>
      </div>
    </div>

    {growthStatus && proposedWeight != null && (
      <div className={`rounded-lg border px-3 py-2 text-xs font-medium ${growthStatusClass}`}>
        {growthStatus}
      </div>
    )}

    {targetWeight != null && (
      <div className="rounded-xl border bg-background/70 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">{i18n.t("batch4.e154cb584579")}</p>
          <p className="font-semibold">{targetWeight} {i18n.t("batch4.1389845b02c0")}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <div>
            <p>{i18n.t("batch4.8368712c026f")}</p>
            <p className="font-semibold text-foreground">
              {remainingWeight != null ? `${remainingWeight} kg` : "—"}
            </p>
          </div>
          <div>
            <p>{i18n.t("batch4.1b90271d66cf")}</p>
            <p className="font-semibold text-foreground">
              {progressPercentage != null ? `${Math.round(progressPercentage)}%` : "—"}
            </p>
          </div>
        </div>
        <div className="h-2 rounded-full bg-muted">
          <div
            className="h-2 rounded-full bg-primary"
            style={{ width: `${progressPercentage != null ? Math.max(0, Math.min(100, progressPercentage)) : 0}%` }}
          />
        </div>
      </div>
    )}
  </div>
)}
</>
)}
{action === "vaccination" && (
  <>
    <Input
      type="text"
      placeholder={i18n.t("batch4.d6bfe7918d9b")}
      value={form.vaccine_name || ""}
      onChange={(e) =>
        setForm({
          ...form,
          vaccine_name: e.target.value,
        })
      }
      className="h-12 text-base"
      data-testid="field-vaccine-name"
    />

    <VoiceTextarea
      placeholder={i18n.t("batch4.512f8825b4cc")}
      value={form.notes || ""}
      onChange={(e) =>
        setForm({
          ...form,
          notes: e.target.value,
        })
      }
    />
  </>
)}
{action === "weigh" && (
  <>
    <Input
      type="number"
      step="0.1"
      placeholder={i18n.t("batch4.d24983206285")}
      value={form.weight_kg || ""}
      onChange={(e) =>
        setForm({
          ...form,
          weight_kg: e.target.value,
        })
      }
    />

    <Input
      type="number"
      step="0.1"
      placeholder={i18n.t("batch4.69fa4490f352")}
      value={form.feed_kg || ""}
      onChange={(e) =>
        setForm({
          ...form,
          feed_kg: e.target.value,
        })
      }
    />

    <VoiceTextarea
      placeholder={i18n.t("batch4.512f8825b4cc")}
      value={form.notes || ""}
      onChange={(e) =>
        setForm({
          ...form,
          notes: e.target.value,
        })
      }
    />
  </>
)}

{action === "vaccination" && (
  <>
    <Input
      type="text"
      placeholder={i18n.t("batch4.d6bfe7918d9b")}
      value={form.vaccine_name || ""}
      onChange={(e) =>
        setForm({
          ...form,
          vaccine_name: e.target.value,
        })
      }
      className="h-12 text-base"
      data-testid="field-vaccine-name"
    />
    <Input
      placeholder={i18n.t("batch4.62f83bf42d3b")}
      value={form.dose || ""}
      onChange={(e) =>
        setForm({
          ...form,
          dose: e.target.value,
        })
      }
      className="h-12 text-base"
    />
    <Input
      type="date"
      placeholder={i18n.t("batch4.5aab6f03b04f")}
      value={form.next_due_date || ""}
      onChange={(e) =>
        setForm({
          ...form,
          next_due_date: e.target.value,
        })
      }
      className="h-12 text-base"
    />
    <VoiceTextarea
      placeholder={i18n.t("batch4.512f8825b4cc")}
      value={form.notes || ""}
      onChange={(e) =>
        setForm({
          ...form,
          notes: e.target.value,
        })
      }
    />
  </>
)}

{action === "health_inspection" && (
  <>
    <Select value={form.condition || "healthy"} onValueChange={(value) => setForm({ ...form, condition: value })}>
      <SelectTrigger className="h-12 text-base">
        <SelectValue placeholder={i18n.t("batch4.2f4979324e03")} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="healthy">{i18n.t("batch4.80ea156e1b83")}</SelectItem>
        <SelectItem value="observe">{i18n.t("batch4.c99a2bcf3e71")}</SelectItem>
        <SelectItem value="treat">{i18n.t("batch4.a92db57e837c")}</SelectItem>
      </SelectContent>
    </Select>
    <Input type="number" step="0.1" placeholder={i18n.t("batch4.294563c25f4e")} value={form.temperature_c || ""} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "medication" && (
  <>
    <Input placeholder={i18n.t("batch4.8bd9cf4070aa")} value={form.medication_name || ""} onChange={(e) => setForm({ ...form, medication_name: e.target.value })} className="h-12 text-base" />
    <Input placeholder={i18n.t("batch4.62f83bf42d3b")} value={form.dose || ""} onChange={(e) => setForm({ ...form, dose: e.target.value })} className="h-12 text-base" />
    <Input placeholder={i18n.t("batch4.7ae7632852dc")} value={form.route || ""} onChange={(e) => setForm({ ...form, route: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "mortality" && (
  <>
    <Input placeholder={i18n.t("batch4.358e14a034cd")} value={form.cause || ""} onChange={(e) => setForm({ ...form, cause: e.target.value })} className="h-12 text-base" />
    <Input placeholder={i18n.t("batch4.7ef537a2316a")} value={form.disposal_method || ""} onChange={(e) => setForm({ ...form, disposal_method: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "breeding" && (
  <>
    <Select value={form.dam_id || ""} onValueChange={(value) => {
      const selected = activeAnimals.find((animal) => animal.id === value);
      setForm({ ...form, dam_id: value, dam_tag: selected?.tag_id || selected?.name || value });
    }}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.9a95a36384c2")} /></SelectTrigger>
      <SelectContent>{activeAnimals.map((animal) => <SelectItem key={animal.id} value={animal.id}>{animal.tag_id || animal.name || animal.id}</SelectItem>)}</SelectContent>
    </Select>
    <Select value={form.sire_id || ""} onValueChange={(value) => {
      const selected = activeAnimals.find((animal) => animal.id === value);
      setForm({ ...form, sire_id: value, sire_tag: selected?.tag_id || selected?.name || value });
    }}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.fd1cf782f0a4")} /></SelectTrigger>
      <SelectContent>{activeAnimals.map((animal) => <SelectItem key={animal.id} value={animal.id}>{animal.tag_id || animal.name || animal.id}</SelectItem>)}</SelectContent>
    </Select>
    <Input type="date" value={form.service_date || ""} onChange={(e) => setForm({ ...form, service_date: e.target.value, date: e.target.value || form.date })} className="h-12 text-base" />
    <Input type="date" value={form.heat_date || ""} onChange={(e) => setForm({ ...form, heat_date: e.target.value })} className="h-12 text-base" placeholder={i18n.t("batch4.64dfd68f1226")} />
    <Select value={(form.method || "natural").toLowerCase()} onValueChange={(value) => setForm({ ...form, method: value })}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.7e47085008e8")} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="natural">{i18n.t("batch4.ea26876daa9a")}</SelectItem>
        <SelectItem value="ai">{i18n.t("batch4.560040c54a3b")}</SelectItem>
      </SelectContent>
    </Select>
    <Input placeholder={i18n.t("batch4.e6d03272a321")} value={form.technician || ""} onChange={(e) => setForm({ ...form, technician: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "pregnancy_check" && (
  <>
    <Select value={form.result || "pregnant"} onValueChange={(value) => setForm({ ...form, result: value })}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.5faa59d4bc37")} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="pregnant">{i18n.t("batch4.11d833a652a2")}</SelectItem>
        <SelectItem value="not_pregnant">{i18n.t("batch4.f353276e0c59")}</SelectItem>
        <SelectItem value="failed">{i18n.t("batch4.25353db6f04b")}</SelectItem>
        <SelectItem value="aborted">{i18n.t("batch4.b1590d210090")}</SelectItem>
        <SelectItem value="uncertain">{i18n.t("batch4.c6a143441b52")}</SelectItem>
      </SelectContent>
    </Select>
    <Input type="date" placeholder={i18n.t("batch4.139dd46acc13")} value={form.estimated_due_date || ""} onChange={(e) => setForm({ ...form, estimated_due_date: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "farrowing" && (
  <>
    <Select value={form.dam_id || ""} onValueChange={(value) => {
      const selected = activeAnimals.find((animal) => animal.id === value);
      setForm({ ...form, dam_id: value, dam_tag: selected?.tag_id || selected?.name || value });
    }}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.9a95a36384c2")} /></SelectTrigger>
      <SelectContent>{activeAnimals.map((animal) => <SelectItem key={animal.id} value={animal.id}>{animal.tag_id || animal.name || animal.id}</SelectItem>)}</SelectContent>
    </Select>
    <Input type="number" placeholder={i18n.t("batch4.d0c31e3e846d")} value={form.litter_size || ""} onChange={(e) => setForm({ ...form, litter_size: e.target.value })} className="h-12 text-base" />
    <Input type="number" placeholder={i18n.t("batch4.93de82546383")} value={form.live_born || ""} onChange={(e) => setForm({ ...form, live_born: e.target.value })} className="h-12 text-base" />
    <Input type="number" placeholder={i18n.t("batch4.3a3a224571d8")} value={form.stillborn || ""} onChange={(e) => setForm({ ...form, stillborn: e.target.value })} className="h-12 text-base" />
    <Input type="number" placeholder={i18n.t("batch4.88373169e5fa")} value={form.mummified || ""} onChange={(e) => setForm({ ...form, mummified: e.target.value })} className="h-12 text-base" />
    <Input type="number" placeholder={i18n.t("batch4.439ff8e6fc5a")} value={form.weak_piglets || ""} onChange={(e) => setForm({ ...form, weak_piglets: e.target.value })} className="h-12 text-base" />
    <Input type="number" placeholder={i18n.t("batch4.320080b9ad01")} value={form.birth_weight || ""} onChange={(e) => setForm({ ...form, birth_weight: e.target.value })} className="h-12 text-base" />
    <Input placeholder={i18n.t("batch4.5e2ba821e480")} value={form.complications || ""} onChange={(e) => setForm({ ...form, complications: e.target.value })} className="h-12 text-base" />
    <Select value={form.assistance_required || "no"} onValueChange={(value) => setForm({ ...form, assistance_required: value })}>
      <SelectTrigger className="h-12 text-base"><SelectValue placeholder={i18n.t("batch4.17836b325826")} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="no">{i18n.t("batch4.8b20a6951162")}</SelectItem>
        <SelectItem value="yes">{i18n.t("batch4.17836b325826")}</SelectItem>
      </SelectContent>
    </Select>
    <VoiceTextarea placeholder={i18n.t("batch4.512f8825b4cc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "plant" && (
  <>
    <Input placeholder={i18n.t("batch4.f8760a38ce49")} value={form.plot_name || ""} onChange={(e) => setForm({ ...form, plot_name: e.target.value })} className="h-12 text-base" />
    <Input placeholder={i18n.t("batch4.c5db3d91c4cb")} value={form.crop || ""} onChange={(e) => setForm({ ...form, crop: e.target.value })} className="h-12 text-base" />
    <Input type="number" min="0" step="0.01" placeholder={i18n.t("batch4.3f0631d97412")} value={form.area_ha || ""} onChange={(e) => setForm({ ...form, area_ha: e.target.value })} className="h-12 text-base" />
    <Input type="date" value={form.date || today} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-12 text-base" />
    <Input type="date" value={form.expected_harvest_date || ""} onChange={(e) => setForm({ ...form, expected_harvest_date: e.target.value })} className="h-12 text-base" />
    <VoiceTextarea placeholder={i18n.t("batch4.70440046a3dc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{(action === "spray" || action === "irrigate" || action === "harvest") && (
  <>
    <Select value={form.plot_id || ""} onValueChange={(value) => setForm({ ...form, plot_id: value })}>
      <SelectTrigger className="h-12 text-base" data-testid="field-crop-plot-select"><SelectValue placeholder={i18n.t("batch4.1381ab06b60e")} /></SelectTrigger>
      <SelectContent>{plots.map((plot) => <SelectItem key={plot.id} value={plot.id}>{plot.name || plot.crop || plot.id}</SelectItem>)}</SelectContent>
    </Select>
    <Input type="date" value={form.date || today} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-12 text-base" />
    {action !== "harvest" && <Input placeholder={i18n.t("batch4.eada007127e6")} value={form.input_name || ""} onChange={(e) => setForm({ ...form, input_name: e.target.value })} className="h-12 text-base" />}
    {action !== "harvest" && <Input type="number" min="0" placeholder={i18n.t("batch4.44f6af694554")} value={form.quantity || ""} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="h-12 text-base" />}
    {action === "harvest" && <Input type="number" min="0" placeholder={i18n.t("batch4.34a79d015463")} value={form.yield_kg || ""} onChange={(e) => setForm({ ...form, yield_kg: e.target.value })} className="h-12 text-base" />}
    <VoiceTextarea placeholder={i18n.t("batch4.70440046a3dc")} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-base" />
  </>
)}

{action === "birth" && (
  <>
    <Select
      value={form.species_id || ""}
      onValueChange={(value) =>
        setForm({
          ...form,
          species_id: value,
        })
      }
    >
      <SelectTrigger
        className="h-12 text-base"
        data-testid="field-birth-species-select"
      >
        <SelectValue placeholder={i18n.t("batch4.83a7238826ab")} />
      </SelectTrigger>

      <SelectContent>
        {species.map((item) => (
          <SelectItem key={item.id} value={item.id}>
            {item.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    <Input
      placeholder={i18n.t("batch4.034a7bae70a5")}
      value={form.dam || ""}
      onChange={(e) =>
        setForm({
          ...form,
          dam: e.target.value,
        })
      }
    />

    <Input
      type="number"
      placeholder={i18n.t("batch4.d0c31e3e846d")}
      value={form.litter_size || ""}
      onChange={(e) =>
        setForm({
          ...form,
          litter_size: e.target.value,
        })
      }
    />

    <VoiceTextarea
      placeholder={i18n.t("batch4.512f8825b4cc")}
      value={form.notes || ""}
      onChange={(e) =>
        setForm({
          ...form,
          notes: e.target.value,
        })
      }
    />
  </>
)}
<Button
    onClick={submitAction}
    disabled={busy}
    className="w-full h-14 text-base font-bold"
    data-testid="field-submit-btn"
>
    {busy ? "Saving..." : "✓ SAVE"}
...
</Button>

</div>
</DialogContent>
</Dialog>
        </div>
    );
}
